"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.wordpressPublishPage = exports.wordpressPublishPageResultSchema = exports.wordpressPublishPageArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Import section schemas from seoArticleWriter for reuse
var headerSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('header'),
    level: zod_1.z.number().min(1).max(6),
    content: zod_1.z.string(),
    id: zod_1.z.string().nullish(),
    keywords: zod_1.z.array(zod_1.z.string()).nullish(),
});
var paragraphSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('paragraph'),
    content: zod_1.z.string(),
    id: zod_1.z.string().nullish(),
});
var imageSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('image'),
    src: zod_1.z.string().url(),
    alt: zod_1.z.string().max(125),
    title: zod_1.z.string().nullish(),
    caption: zod_1.z.string().nullish(),
    width: zod_1.z.number().min(1).nullish(),
    height: zod_1.z.number().min(1).nullish(),
    formats: zod_1.z
        .object({
        webp: zod_1.z.string().url().nullish(),
        avif: zod_1.z.string().url().nullish(),
    })
        .nullish(),
});
var listSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('list'),
    style: zod_1.z.enum(['ordered', 'unordered']),
    items: zod_1.z.array(zod_1.z.string()),
});
var tableSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('table'),
    caption: zod_1.z.string().nullish(),
    headers: zod_1.z.array(zod_1.z.string()),
    rows: zod_1.z.array(zod_1.z.array(zod_1.z.string())),
});
var faqSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('faq'),
    items: zod_1.z.array(zod_1.z.object({
        question: zod_1.z.string(),
        answer: zod_1.z.string(),
        id: zod_1.z.string().nullish(),
    })),
});
var codeSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('code'),
    language: zod_1.z.string().nullish(),
    content: zod_1.z.string(),
    caption: zod_1.z.string().nullish(),
});
var blockquoteSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('blockquote'),
    content: zod_1.z.string(),
    citation: zod_1.z.string().nullish(),
    url: zod_1.z.string().url().nullish(),
});
var contentSectionSchema = zod_1.z.union([
    headerSectionSchema,
    paragraphSectionSchema,
    imageSectionSchema,
    listSectionSchema,
    tableSectionSchema,
    faqSectionSchema,
    codeSectionSchema,
    blockquoteSectionSchema,
]);
// Discriminated union for content input
var htmlContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('html'),
    content: zod_1.z.string().describe('The page content (HTML format)'),
});
var markdownContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('markdown'),
    content: zod_1.z.string().describe('The page content (Markdown format) - will be converted to HTML'),
});
var sectionsContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('sections'),
    sections: zod_1.z
        .array(contentSectionSchema)
        .min(1)
        .describe('Structured content sections (compatible with seoArticleWriter output)'),
});
// Argument schema with discriminated union
exports.wordpressPublishPageArgsSchema = zod_1.z.object({
    title: zod_1.z.string().describe('The page title'),
    contentInput: zod_1.z
        .discriminatedUnion('contentType', [
        htmlContentSchema,
        markdownContentSchema,
        sectionsContentSchema,
    ])
        .describe('Page content - HTML, Markdown, or structured sections from seoArticleWriter'),
    status: zod_1.z
        .enum(['publish', 'draft', 'pending', 'private'])
        .default('publish')
        .describe('The page status (publish, draft, pending, or private)'),
    slug: zod_1.z.string().nullish().describe('Optional URL-friendly slug for the page'),
    excerpt: zod_1.z.string().nullish().describe('Optional brief description of the page'),
    featuredMediaId: zod_1.z.number().nullish().describe('Optional WordPress media ID for featured image'),
    parentId: zod_1.z.number().nullish().describe('Optional parent page ID for hierarchical pages'),
    menuOrder: zod_1.z.number().nullish().describe('Optional menu order for the page'),
    commentStatus: zod_1.z
        .enum(['open', 'closed'])
        .nullish()
        .describe('Optional comment status (open or closed)'),
    pingStatus: zod_1.z
        .enum(['open', 'closed'])
        .nullish()
        .describe('Optional ping/trackback status (open or closed)'),
});
exports.wordpressPublishPageResultSchema = zod_1.z.object({
    id: zod_1.z.number().describe('The WordPress page ID'),
    url: zod_1.z.string().describe('The public URL of the published page'),
    status: zod_1.z.string().describe('The page status (publish, draft, pending, or private)'),
    title: zod_1.z.string().describe('The page title'),
    slug: zod_1.z.string().describe('The URL-friendly slug'),
});
// Tool definition
exports.wordpressPublishPage = (0, common_1.createToolMetadata)({
    id: 'wordpressPublishPage',
    displayName: 'WordPress Publish Page',
    displayDescription: 'Create and publish a new page on WordPress',
    category: 'communication',
    color: common_1.BRAND_COLORS.WORDPRESS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.WORDPRESS),
    icon: 'wordpress',
    argsSchema: exports.wordpressPublishPageArgsSchema,
}, exports.wordpressPublishPageResultSchema);
